import React, { useEffect, useState } from 'react';
import { Offcanvas, Card, Button, Spinner, Alert, InputGroup, Form } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useToast } from 'context/providers/ToastProvider';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

const OffcanvasPagosDuplicados = ({ show, onHide, referencia, token, setShowModalConciliar, setID_Pago, refreshTrigger }) => {
    const { showErrorToast } = useToast();
    const [pagos, setPagos] = useState([]);
    const [loading, setLoading] = useState(false);

    // Pagination states
    const [pageNumber, setPageNumber] = useState(1);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const [hasMore, setHasMore] = useState(true);
    const [searchText, setSearchText] = useState('');
    const pageSize = 7;

    useEffect(() => {
        if (show && referencia) {
            setSearchText('');
            setPageNumber(1);
            setHasMore(true);
            setPagos([]);
            fetchPagos(referencia, 1, '');
        } else {
            setPagos([]);
            setSearchText('');
        }
    }, [show, referencia, refreshTrigger]);

    useEffect(() => {
        if (show && referencia) {
            const delayDebounceFn = setTimeout(() => {
                setPageNumber(1);
                setHasMore(true);
                setPagos([]);
                fetchPagos(referencia, 1, searchText);
            }, 500);
            return () => clearTimeout(delayDebounceFn);
        }
    }, [searchText]);

    useEffect(() => {
        if (pageNumber > 1 && hasMore) {
            fetchPagos(referencia, pageNumber, searchText);
        }
    }, [pageNumber]);

    const fetchPagos = async (vRef, page, search = '') => {
        if (page === 1) setLoading(true);
        else setIsFetchingMore(true);

        try {
            const response = await fetch(`/api/AdministracionPagos/obtenerPagosPorReferencia?vReferencia=${encodeURIComponent(vRef)}&pageNumber=${page}&pageSize=${pageSize}&searchText=${encodeURIComponent(search)}`, {
                method: 'GET',
                headers: {
                    'Authorization': 'Bearer ' + token,
                    'Content-Type': 'application/json'
                }
            });

            if (response.ok) {
                const responseType = await response.json();
                if (responseType.iResponseType === 1) {
                    const data = responseType.detalles ? JSON.parse(responseType.detalles) : [];

                    if (data.length < pageSize) {
                        setHasMore(false);
                    }

                    if (page === 1) {
                        setPagos(data);
                    } else {
                        setPagos(prev => [...prev, ...data]);
                    }
                } else {
                    showErrorToast(responseType.vMessage);
                }
            } else {
                showErrorToast('Error de conexión');
            }
        } catch (error) {
            showErrorToast(error.message);
        }

        if (page === 1) setLoading(false);
        else setIsFetchingMore(false);
    };

    const formatAmount = (amount) => {
        return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount || 0);
    };

    return (
        <Offcanvas show={show} onHide={onHide} placement="end" style={{ width: '450px' }}>
            <Offcanvas.Header closeButton className="border-bottom pb-3">
                <Offcanvas.Title className="w-100">
                    <div className="d-flex align-items-center gap-2 mb-1">
                        <FontAwesomeIcon icon="exclamation-triangle" className="text-danger" />
                        <h5 className="mb-0 fw-bold text-danger">Gestión de Duplicados</h5>
                    </div>
                    <div className="fs--1 text-600 fw-normal">Referencia: <span className="fw-bold text-dark">{referencia}</span></div>
                </Offcanvas.Title>
            </Offcanvas.Header>
            <Offcanvas.Body className="d-flex flex-column p-0 bg-light">
                <div
                    className="p-4 flex-grow-1"
                    style={{ overflowY: 'auto' }}
                    onScroll={(e) => {
                        const { scrollTop, scrollHeight, clientHeight } = e.target;
                        if (scrollTop + clientHeight >= scrollHeight - 20 && !isFetchingMore && hasMore) {
                            setPageNumber(prev => prev + 1);
                        }
                    }}
                >

                    <Alert variant="danger" className="border-0 shadow-sm rounded-3 py-2 px-3 mb-4 d-flex align-items-start gap-2" style={{ backgroundColor: '#fff5f5' }}>
                        <FontAwesomeIcon icon="info-circle" className="mt-1" />
                        <span className="fs--1 lh-sm text-danger" style={{ fontWeight: '500' }}>
                            Se han detectado múltiples transacciones con esta referencia. Por favor, valide cuál es el movimiento correcto.
                        </span>
                    </Alert>

                    <h6 className="text-uppercase text-500 mb-2 fs--1 fw-bold tracking-wide">Buscar Pago</h6>
                    <InputGroup className="mb-4 shadow-sm">
                        <InputGroup.Text className="bg-white border-end-0">
                            <FontAwesomeIcon icon="search" className="text-400" />
                        </InputGroup.Text>
                        <Form.Control
                            type="text"
                            placeholder="Buscar por número de pago..."
                            className="border-start-0 ps-0"
                            value={searchText}
                            onChange={(e) => setSearchText(e.target.value)}
                        />
                    </InputGroup>

                    <h6 className="text-uppercase text-500 mb-3 fs--1 fw-bold tracking-wide">Desglose de Movimientos</h6>

                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="danger" />
                        </div>
                    ) : pagos.length === 0 ? (
                        <div className="text-center py-5 px-3">
                            <FontAwesomeIcon icon="search" className="text-300 mb-3" style={{ fontSize: '3rem' }} />
                            <h6 className="text-500 mb-1">No se encontraron pagos</h6>
                            {searchText ? (
                                <p className="text-500 fs--1 mb-0">No hay resultados para la búsqueda "{searchText}".</p>
                            ) : (
                                <p className="text-500 fs--1 mb-0">Esta referencia no tiene pagos registrados.</p>
                            )}
                        </div>
                    ) : (
                        <div className="d-flex flex-column gap-3">
                            {pagos.map((pago) => {
                                // Determinamos el estado del badge manejando booleanos y enteros
                                let badge;
                                const isActive = pago.bIsActive === true || pago.bIsActive == 1;

                                if (!isActive) {
                                    badge = { text: 'CANCELADO', bg: '#e3342f', color: '#ffffff' }; // Rojo claro/puro
                                } else if (pago.idCatCuentaBancaria > 0) {
                                    badge = { text: 'VERIFICADO', bg: '#38c172', color: '#ffffff' }; // Verde claro/puro
                                } else {
                                    badge = { text: 'PENDIENTE', bg: '#f6993f', color: '#ffffff' }; // Naranja claro/puro
                                }

                                return (
                                    <Card
                                        key={pago.idPago}
                                        className="border shadow-none mb-3"
                                        style={{ borderRadius: '12px', borderColor: '#e4e5e9' }}
                                    >
                                        <Card.Body className="p-3">
                                            {/* Top Section */}
                                            <div className="d-flex justify-content-between align-items-start mb-3">
                                                <div className="d-flex gap-3">
                                                    {/* Icon Box */}
                                                    <div
                                                        className="d-flex align-items-center justify-content-center rounded"
                                                        style={{ width: '48px', height: '48px', backgroundColor: '#f0f4f8', color: '#5c6e8a' }}
                                                    >
                                                        <FontAwesomeIcon icon="file-invoice-dollar" className="fs-1" />
                                                    </div>

                                                    {/* Title and Date */}
                                                    <div className="d-flex flex-column justify-content-center">
                                                        <div className="d-flex align-items-center gap-2 mb-1">
                                                            <h6 className="fw-bolder text-dark mb-0 fs-0" style={{ letterSpacing: '-0.5px' }}>
                                                                Pago #{pago.idPago}
                                                            </h6>
                                                            {pago.idOrden && (
                                                                <Link to={`/ordenes/detalle/${pago.idOrden}`} className="text-decoration-none">
                                                                    <span className="badge rounded-pill fw-bold text-600 px-2 py-1 text-uppercase" style={{ backgroundColor: '#e9ecef', fontSize: '10px' }}>
                                                                        ORDEN #{pago.idOrden}
                                                                    </span>
                                                                </Link>
                                                            )}
                                                        </div>
                                                        <span className="text-600 fs--1 fw-semi-bold">
                                                            <FontAwesomeIcon icon="calendar-alt" className="me-2 text-500" />
                                                            {pago.dFechaFormateada ? pago.dFechaFormateada.replace('.', '') : 'Sin Fecha'}
                                                        </span>
                                                    </div>
                                                </div>

                                                {/* Status Badge */}
                                                <span
                                                    className="px-2 py-1 rounded-pill fw-bold text-uppercase"
                                                    style={{ backgroundColor: badge.bg, color: badge.color, fontSize: '10px', letterSpacing: '0.5px' }}
                                                >
                                                    {badge.text}
                                                </span>
                                            </div>

                                            {/* Seller Section */}
                                            {pago.vNombreVendedor && (
                                                <div className="mb-3 text-600 fs--1 fw-semi-bold text-uppercase d-flex align-items-center">
                                                    <FontAwesomeIcon icon="user" className="me-2 text-500" />
                                                    VENDEDOR: {pago.vNombreVendedor}
                                                </div>
                                            )}

                                            <hr className="bg-200 mb-3" style={{ opacity: 0.5 }} />

                                            {/* Bottom Section */}
                                            <div className="d-flex justify-content-between align-items-center">
                                                <h4 className="mb-0 fw-bolder text-dark" style={{ letterSpacing: '-0.5px' }}>
                                                    {formatAmount(pago.dImportePago, pago.vMoneda)} <span className="fs--1 fw-bold text-500 ms-1">{pago.vMoneda}</span>
                                                </h4>
                                                <div
                                                    className="fs--1 fw-bold text-primary cursor-pointer"
                                                    onClick={() => {
                                                        setID_Pago(pago.idPago);
                                                        setShowModalConciliar(true);
                                                    }}
                                                >
                                                    Ver detalle
                                                </div>
                                            </div>
                                        </Card.Body>
                                    </Card>
                                );
                            })}
                            {isFetchingMore && (
                                <div className="text-center py-2">
                                    <Spinner animation="border" variant="danger" size="sm" />
                                </div>
                            )}
                        </div>
                    )}
                </div>

                <div className="p-3 border-top bg-white d-flex flex-column gap-2">
                    <Button variant="light" className="fw-bold border py-2 text-dark" onClick={onHide}>
                        Regresar a la Lista
                    </Button>
                </div>
            </Offcanvas.Body>
        </Offcanvas>
    );
};

OffcanvasPagosDuplicados.propTypes = {
    show: PropTypes.bool.isRequired,
    onHide: PropTypes.func.isRequired,
    referencia: PropTypes.string,
    token: PropTypes.string.isRequired,
    setShowModalConciliar: PropTypes.func.isRequired,
    setID_Pago: PropTypes.func.isRequired,
    refreshTrigger: PropTypes.number
};

export default OffcanvasPagosDuplicados;
